import { useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import {
  MiDetailFields,
  MiDetailPageHeader,
  MiTransferList,
} from "@miplatform/components";
import {
  INPUT_TYPES,
  PLATFORM_MODULES_OPTIONS,
  specialCharacterRegex,
} from "@miplatform/constants";
import { useEdit, useRouter } from "@miplatform/hooks";
import { mdiChevronLeft, mdiFormatListBulleted } from "@mdi/js";
import { Footer } from "@mibuildweb/components";
import { Box } from "@mui/material";
import { useRoleDetailsViewModel } from "./useRoleDetailsViewModel";

const useStyles = makeStyles()((theme) => ({
  container: {
    height: "inherit",
    overflowY: "scroll",
    margin: 5,
    borderRadius: 10,
    padding: 20,
    backgroundColor: theme.palette.common.white,
  },
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "inherit",
  },
}));

export function RoleDetails() {
  const route = useRouter();
  const { id: roleId } = route.params;

  const {
    permissions,
    assignedPermissions,
    getUserRoleAndAllPermissions,
    setAssignedPermissions,
    setPermissions,
    updateRole,
    role,
    stateManager,
  } = useRoleDetailsViewModel();

  const { classes } = useStyles();

  useEffect(() => {
    getUserRoleAndAllPermissions(roleId);
  }, []);

  const onUpdate = async (data) => {
    if (data?.permissionNames) {
      data.permissionNames = assignedPermissions.map((e) => e.name);
    }
    const response = await updateRole(roleId, data);
    if (response) {
      getUserRoleAndAllPermissions(roleId);
      edit.reset();
    }
  };

  const detailFields = [
    {
      label: "Role Name",
      name: "name",
      required: true,
      width: "195px",
      flexGrow: 0,
      validationRule: z
        .string()
        .min(1, "Role name is required.")
        .regex(specialCharacterRegex, {
          message: "Role name cannot contain special characters",
        }),
    },
    {
      label: "Description",
      name: "description",
      required: true,
      width: "100%",
      validationRule: z.string().min(1),
    },
    {
      label: "Modules",
      name: "modules",
      required: false,
      width: "100%",
      multiple: true,
      options: PLATFORM_MODULES_OPTIONS,
      fieldType: INPUT_TYPES.MULTI_SELECT,
    },
  ];

  const edit = useEdit(role, detailFields);

  const onAssginedPermissionsChange = (data) => {
    edit.update({ permissionNames: data });
    setAssignedPermissions(data);
  };

  const onAvailablePermissionsChange = (data) => {
    setPermissions(data);
  };

  const resetFormData = () => {
    edit.reset();
    setAssignedPermissions(role?.permissionNames);
    setPermissions(role?.availablePermissions);
  };

  return (
    <div className={classes.contentContainer}>
      <MiDetailPageHeader
        title={`Role: ${role?.name || ""}`}
        titleIcon={mdiChevronLeft}
        loading={stateManager.isBusy()}
      />
      <Box className={classes.container}>
        <MiDetailFields
          detailFields={detailFields}
          headerProps={{
            icon: mdiFormatListBulleted,
            title: "Detail Fields",
          }}
          edit={edit}
        />
        <div className={classes.contentSection}>
          <MiTransferList
            id="assigned-permissions"
            headerLeft="Available Permissions"
            headerRight="Assigned Permissions"
            rightHandler={onAssginedPermissionsChange}
            leftHandler={onAvailablePermissionsChange}
            listItemsLeft={permissions}
            listItemsRight={assignedPermissions}
            readOnly={false}
          />
        </div>
      </Box>
      <Footer
        show={edit.isDirty()}
        handleSaveClick={() => onUpdate(edit.handleSubmit())}
        handleCancelClick={resetFormData}
        disableSave={!edit.isValid() || stateManager.isBusy()}
      />
    </div>
  );
}
